import { state, style, trigger } from '@angular/animations';
import { AsyncPipe, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { FahrzeugbesichtigungDialogComponent } from '@app/shared/components/dialog/fahrzeugbesichtigung-dialog/fahrzeugbesichtigung-dialog.component';
import { NoDataComponent } from '@app/shared/components/no-data/no-data.component';
import { ReminderComponent } from '@app/shared/components/reminder/reminder.component';
import { HasRightsDirective } from '@app/shared/directives/has-rights.directive';
import { ThrottleEventDirective } from '@app/shared/directives/throttle-event.directive';
import { FormatAdressPipe } from '@app/shared/pipes/formatadress.pipe';
import { FormatDatePipe } from '@app/shared/pipes/formatdate.pipe';
import { Action } from '@app/shared/services/ui/dialog/dialog.types';
import { ExpandableRow, ListTyp } from '@app/views/vertriebscockpit/offerteantraege/offerteantraege.types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Esign, EsignHauptStatus, Payload, PolizzeResult } from '@o30/entities';
import { of } from 'rxjs';
import { first, map, switchMap, take } from 'rxjs/operators';
import { BasicPolizzeListComponent } from '../basic-polizze-list/basic-polizze-list.component';

@UntilDestroy()
@Component({
  selector:    'app-antraege',
  templateUrl: './antraege.component.html',
  styleUrls:   ['./antraege.component.scss'],
  animations:  [
    trigger('detailExpand', [
      state('collapsed', style({
        display: 'none'
      })),
      state('expanded', style({
        height:     '*',
        visibility: 'visible'
      }))
    ])
  ],
  standalone:  true,
  imports: [
    NgIf,
    MatTable,
    MatSort,
    NgFor,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatIcon,
    ThrottleEventDirective,
    MatTooltip,
    HasRightsDirective,
    NgTemplateOutlet,
    NgClass,
    ReminderComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    MatProgressSpinner,
    NoDataComponent,
    FormatDatePipe,
    FormatAdressPipe,
    AsyncPipe
  ]
})
export class AntraegeComponent extends BasicPolizzeListComponent implements OnInit,
                                                                            OnDestroy {
  @Input()
  isLebenAenderungsoffert = false;

  public deleteRow(row: PolizzeResult) {
    if (this.isLebenAenderungsoffert) {
      this.deleteLebenAenderungsOffert(row);
    } else {
      this.spoilAntrag(row);
    }
  }

  private deleteLebenAenderungsOffert(row: PolizzeResult) {
    this.basicReset(row,
      'Soll das Änderungsoffert gelöscht werden?',
      () => of({data: true} as Payload<boolean>),
      this.dataService.deletePolizze.bind(this.dataService));
  }

  private spoilAntrag(row: PolizzeResult): void {
    this.basicReset(row, 'Wollen Sie den Antrag verderben?',
      this.dataService.checkPolizzeForVerderben.bind(this.dataService),
      this.dataService.savePolizzeAntragStorno.bind(this.dataService), ListTyp.VERDORBENER_ANTRAG);
  }

  public esignDialog(row: PolizzeResult): void {
    const status = row.esign.status;
    switch (status) {
      case EsignHauptStatus.ENVELOPE_EXISTS:
      case EsignHauptStatus.NEW:
        this.checkBesichtigungAndOpenAnalyse(row);
        break;
      case EsignHauptStatus.FINISHED:
      default:
        this.messageService.showDialog({
          id:   'UW-unterschrieben',
          text: 'Antrag bereits unterschrieben.',
          icon: 'warning'
        });
    }
  }

  private checkBesichtigungAndOpenAnalyse(polizzeResult: PolizzeResult) {
    this.dataService.checkPolizzeRequiresBesichtigung(polizzeResult.id)
      .pipe(
        take(1), untilDestroyed(this),
        map(_payload => _payload.data),
        switchMap(_requiresBesichtigung => _requiresBesichtigung
          ? this.openBesichtigungDialog(polizzeResult) : of({status: 'success'})
        )
      )
      .subscribe(_dialogResult => {
        if (_dialogResult && _dialogResult.status === 'success') {
          this.openAnalyse(polizzeResult);
        }
      });
  }

  private openBesichtigungDialog(polizzeResult: PolizzeResult) {
    return this.reactiveDialogService.open(FahrzeugbesichtigungDialogComponent,
        {},
        {}, {
          polizzeId: polizzeResult.id,
          personId:  polizzeResult.personId
        })
      .afterClosed();
  }

  private openAnalyse(row: PolizzeResult): void {
    let readOnly = row.esign.status === EsignHauptStatus.ENVELOPE_EXISTS;
    this.dialogService.displayPrintDialog(row.esign, row.id, readOnly)
      .afterClosed()
      .pipe(
        take(1),
        untilDestroyed(this))
      .subscribe(_result => {
        if (_result?.data) {
          readOnly = _result.data.status === EsignHauptStatus.ENVELOPE_EXISTS;
          if (readOnly) {
            this.showURLDialog(_result.data, row.id);
          } else {
            this.llapi.patchValue('layout.spinner', true);
            this.dataService.createEsignAntragUnterschriftenProzess(row.id, _result.data)
              .pipe(
                first(),
                untilDestroyed(this))
              .subscribe(_payload => {
                this.llapi.patchValue('layout.spinner', false);
                if (this.messageService.handlePayloadReturnSuccess(_payload)) {
                  this.showURLDialog(_result.data, row.id);
                } else if (_result.status === Action.RESET) {
                  this.loadList();
                }
              });
          }
        } else if (_result && _result.status === Action.RESET) {
          this.loadList();
        }
      });
  }

  public getHauptStatusCSS(row: PolizzeResult): string {
    // Grün: EsignHauptStatus.FINISHED → Meldung, ist fertig
    // Rot: EsignHauptStatus.NEW → oder kein Eintrag, wie gehabt
    // Gelb: EsignHauptStatus.ENVELOPE_EXISTS → Esigndialog
    const status = row?.esign?.status || EsignHauptStatus.NEW;
    switch (status) {
      case EsignHauptStatus.ENVELOPE_EXISTS:
        return 'esign_yellow';
      case EsignHauptStatus.NEW:
        return 'esign_red';
      case EsignHauptStatus.FINISHED:
        return 'esign_green';
      case EsignHauptStatus.EXPIRED:
        return 'esign_red';
      default:
        return 'esign_yellow';
    }
  }

  private showURLDialog(esign: Esign, polizzeId: number): void {
    this.dialogService.displayEsignLinkDialog(esign, polizzeId)
      .afterClosed()
      .pipe(
        take(1),
        untilDestroyed(this))
      .subscribe(() => {
        this.loadList();
      });
  }

  get displayedColumns(): string[] {

    // Bei Lebenänderungsofferte auch die spalte 'variante' anzeigen. Sonst ausblenden
    const columns = this.tableColumns.map(c => c.columnDef)
      .filter(columnDef => columnDef !== 'variante' || this.isLebenAenderungsoffert);

    columns.unshift('expand');

    if (this.bearbeitenErlaubt) {
      columns.push('aktionen');
    }
    return columns;
  }

  getAdditionalColumns() {
    return [
      {
        columnDef: 'variante',
        header:    'Offert Variante',
        cell:      (row: ExpandableRow<PolizzeResult>) => `${row.data.variante}` + (row.data.notiz ? '\n' + (row.data.notiz.length > 10 ? row.data.notiz.substring(0, 10) + '...' : row.data.notiz) : '')
      }
    ];
  }

  // @Override
  getAnzRowsPerEntry(): number {
    return 2;
  }

  isExpired(polizzeResult: PolizzeResult): boolean {
    return polizzeResult?.esign?.status === EsignHauptStatus.EXPIRED;
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--tooltips--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  getTooltipTextSpoil(polizzeResult: PolizzeResult): string {
    if (this.isLebenAenderungsoffert) {
      return 'Änderungsoffert löschen';
    }
    return this.isStornoBeantragt(polizzeResult)
      ? 'Antrag zum Verderben in VT beantragt'
      : 'Antrag verderben';
  }

  getTooltipTextEsign(polizzeResult: PolizzeResult): string {
    return this.isStornoBeantragt(polizzeResult)
      ? 'Antrag zum Verderben in VT beantragt'
      : this.getTooltipTextUnterschriftenProzess(polizzeResult);
  }

  getTooltipTextUnterschriftenProzess(polizzeResult: PolizzeResult) {
    return this.isExpired(polizzeResult)
      ? 'Unterschriftenprozess abgelaufen - bitte Rücksprache mit der Hotline'
      : 'Unterschriftenprozess aufrufen';
  }

  antragChange(row: PolizzeResult) {
    this.polizzeChange(row, 'antrag');
  }

  isAntragChangeDisabled(row: PolizzeResult) {
    return row.esign.status !== EsignHauptStatus.NEW;
  }

  sendDownloadAntrag(row: PolizzeResult) {
    this.peziService.openPeziWithLogin(`/polizze/${row.id}?openPrintDialog=printSendAntrag`, this.destroyRef);
  }
}
